import { useEffect } from 'react';

/**
 * Drops a filter selection once its option is no longer offered.
 *
 * Option lists that derive from the visible population can shrink at runtime —
 * an admin unticking "afficher les visites hors périmètre", or switching to the
 * client view, removes the rows that put ADMIN in the list. A selection left
 * pointing at a vanished option keeps filtering: every tab empties while the
 * control shows nothing to explain it, and on the single-choice selector the
 * control falls back to displaying "Tous univers" over data filtered to
 * something else entirely.
 *
 * @param {Array<{value: *}>} options currently offered
 * @param {*} selected current selection: an array for a multi-select, a scalar
 *   for a single one
 * @param {Function} onPrune called with the surviving selection (the pruned
 *   array, or `fallback` for a scalar) only when something actually changed
 * @param {*} fallback value a scalar selection reverts to
 */
const usePrunedSelection = (options, selected, onPrune, fallback = null) => {
  useEffect(() => {
    const offered = new Set(options.map((option) => option.value));

    if (Array.isArray(selected)) {
      const kept = selected.filter((option) => offered.has(option.value));
      if (kept.length !== selected.length) onPrune(kept);
      return;
    }

    // A scalar with no options yet is not stale — it is a list still loading.
    if (!options.length) return;
    if (selected !== fallback && !offered.has(selected)) onPrune(fallback);
  }, [options, selected, onPrune, fallback]);
};

export default usePrunedSelection;
